import { IconButton, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { hexToRGBA } from "@utils";
import { StarSVG } from "@components/svgs";

type MSTableFavouritedButtonProps = {
  isFavourite?: boolean;
  isDisabled?: boolean;
  showDisabledStyles?: boolean;
  onHandleFavoriteStatus: (favoriteStatus: boolean) => Promise<boolean>;
};

export const MSTableFavouritedButton = ({
  isFavourite = false,
  isDisabled = false,
  showDisabledStyles = true,
  onHandleFavoriteStatus,
}: MSTableFavouritedButtonProps) => {
  const { shadows, palette } = useTheme();
  const [favoriteStatus, setFavoriteStatus] = useState(isFavourite);

  const onFavoriteButtonClick = async () => {
    if (isDisabled) return;

    const response = await onHandleFavoriteStatus(!favoriteStatus);

    if (response) setFavoriteStatus(!favoriteStatus);
  };

  const fillColor = useMemo(() => {
    if (isDisabled && showDisabledStyles && !favoriteStatus) {
      return palette.grey[300];
    }

    return favoriteStatus ? "#FFF500" : palette.common.white;
  }, [isDisabled, showDisabledStyles, favoriteStatus, palette]);

  const strokeColor = useMemo(() => {
    if (isDisabled && showDisabledStyles && !favoriteStatus) {
      return palette.grey[300];
    }

    return favoriteStatus
      ? palette.warning[800]
      : hexToRGBA(palette.error.main, 0.5);
  }, [isDisabled, showDisabledStyles, favoriteStatus, palette]);

  useEffect(() => {
    setFavoriteStatus(isFavourite);
  }, [isFavourite]);

  return (
    <IconButton
      onClick={onFavoriteButtonClick}
      sx={{
        p: 0,
        width: 24,
        height: 24,
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: favoriteStatus
          ? hexToRGBA(palette.error.main, 0.1)
          : palette.common.white,
        borderColor: palette.error[100],
        boxShadow: shadows[1],
        "&>svg>path": {
          fill: fillColor,
          stroke: strokeColor,
        },
      }}
    >
      <StarSVG width={20} height={20} />
    </IconButton>
  );
};
